import {
  Component,
  inject,
  OnInit,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom, forkJoin } from 'rxjs';
import {
  AuthenticationService,
  AuthRedirectState,
} from '../../authentication/authentication.service';
import { EmbeddedSessionService } from '../../embedded/embedded-session.service';
import {
  SalesforceLaunchState,
  SalesforceLaunchStateService,
} from './salesforce-launch-state.service';
import { InquiryService } from '../../services/api/inquiry.service';
import { BrandingInvitationService } from '../../services/api/branding-invitation.service';
import { CompanySettingsService } from '../../services/api/company-settings.service';
import {
  Invitation,
  InvitationLanguage,
  InvitationMethod,
  InvitationType,
} from '../../model/inquiry/invitation';
import { CreateInquiryRequest } from '../../model/inquiry/request/create-new-scheduled-inquiry';
import { RoleService } from '../../authentication/role.service';
import { BackendRole } from '../../authentication/backend-role';

@Component({
  selector: 'app-salesforce-launch',
  templateUrl: './salesforce-launch.component.html',
  styleUrl: './salesforce-launch.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [BrandingInvitationService, InquiryService],
})
export class SalesforceLaunchComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthenticationService);
  private readonly embeddedSessionService = inject(EmbeddedSessionService);
  private readonly salesforceLaunchStateService = inject(
    SalesforceLaunchStateService,
  );
  private readonly inquiryService = inject(InquiryService);
  private readonly brandingInvitationService = inject(
    BrandingInvitationService,
  );
  private readonly companySettingsService = inject(CompanySettingsService);
  private readonly roleService = inject(RoleService);

  protected readonly loading = signal(true);
  protected readonly errorMessage = signal<string | null>(null);

  async ngOnInit(): Promise<void> {
    const launch =
      this.getLaunchFromHash() ??
      this.getLaunchFromQuery() ??
      this.salesforceLaunchStateService.getPendingLaunch();

    if (!launch) {
      this.loading.set(false);
      this.errorMessage.set('Missing Salesforce launch parameters.');
      return;
    }

    this.salesforceLaunchStateService.setPendingLaunch(launch);

    try {
      const currentUser = await firstValueFrom(this.authService.getUser());
      if (!currentUser || currentUser.expired) {
        await this.authService.login({
          extraQueryParams: {
            kc_idp_hint: 'salesforce',
          },
          state: {
            postLoginPath: '/integration/salesforce',
          } satisfies AuthRedirectState,
        });
        return;
      }

      if (!this.isAuthorizedSalesforceUser()) {
        this.salesforceLaunchStateService.clearPendingLaunch();
        await this.router.navigate(['/integration/salesforce/not-authorized'], {
          replaceUrl: true,
        });
        return;
      }

      const invitation = await this.resolveInvitation(launch);
      const inquiry = await firstValueFrom(
        this.inquiryService.createNewInquiry({
          email: this.trimToNull(launch.email),
          phoneNumber: this.trimToNull(launch.phoneNumber),
          externalCustomerId: this.trimToNull(launch.externalCustomerId),
          invitation,
        } as CreateInquiryRequest),
      );

      if (!inquiry?.inquiryIdentifier) {
        this.errorMessage.set('Unable to create the Salesforce inquiry.');
        this.loading.set(false);
        return;
      }

      this.embeddedSessionService.enable();
      this.salesforceLaunchStateService.clearPendingLaunch();
      await this.router.navigate(
        ['/video-appointment', inquiry.inquiryIdentifier],
        {
          replaceUrl: true,
        },
      );
    } catch {
      this.errorMessage.set('Unable to start the Salesforce call.');
      this.loading.set(false);
    }
  }

  private getLaunchFromHash(): SalesforceLaunchState | null {
    const hash = window.location.hash.replace(/^#/, '').trim();
    if (!hash) {
      return null;
    }

    const params = new URLSearchParams(hash.replace(/\+/g, '%2B'));
    return this.toLaunchState(params);
  }

  private getLaunchFromQuery(): SalesforceLaunchState | null {
    const queryParams = this.route.snapshot.queryParamMap;
    const params = new Map<string, string>();
    for (const key of [
      'tenantIdentifier',
      'recordId',
      'objectApiName',
      'phoneNumber',
      'email',
      'externalCustomerId',
      '__tenant__',
    ]) {
      const value = queryParams.get(key);
      if (value) {
        params.set(key, value);
      }
    }

    return this.toLaunchState(params);
  }

  private toLaunchState(params: {
    get(key: string): string | null | undefined;
  }): SalesforceLaunchState | null {
    const tenantIdentifier =
      params.get('tenantIdentifier') ?? params.get('__tenant__');
    const recordId = params.get('recordId');
    const objectApiName = params.get('objectApiName');

    if (!tenantIdentifier || !recordId || !objectApiName) {
      return null;
    }

    return {
      tenantIdentifier,
      recordId,
      objectApiName,
      phoneNumber: this.trimToNull(params.get('phoneNumber')),
      email: this.trimToNull(params.get('email')),
      externalCustomerId: this.trimToNull(params.get('externalCustomerId')),
    };
  }

  private async resolveInvitation(
    launch: SalesforceLaunchState,
  ): Promise<Invitation> {
    const [companySettings, brandingInvitations] = await firstValueFrom(
      forkJoin([
        this.companySettingsService.loadCompanyData(),
        this.brandingInvitationService.getBrandingInvitations(),
      ]),
    );
    const method = this.resolveInvitationMethod(launch);
    const defaultBrandingInvitation = brandingInvitations.find(
      (invitation) => invitation.isDefault && !!invitation.text?.trim(),
    );

    if (defaultBrandingInvitation?.text?.trim()) {
      return {
        language: InvitationLanguage.Unknown,
        type: InvitationType.Custom,
        method,
        text: defaultBrandingInvitation.text.trim(),
      } as Invitation;
    }

    const companyDisplayName = companySettings.displayName?.trim() || 'Firma';
    return {
      language: InvitationLanguage.German,
      type: InvitationType.Default,
      method,
      text: this.getDefaultGermanText(companyDisplayName),
    } as Invitation;
  }

  private resolveInvitationMethod(
    launch: SalesforceLaunchState,
  ): InvitationMethod {
    if (this.trimToNull(launch.phoneNumber)) {
      return InvitationMethod.Sms;
    }

    if (this.trimToNull(launch.email)) {
      return InvitationMethod.Email;
    }

    return InvitationMethod.None;
  }

  private isAuthorizedSalesforceUser(): boolean {
    return (
      this.roleService.hasRole(BackendRole.Agent) ||
      this.roleService.hasRole(BackendRole.Manager) ||
      this.roleService.hasRole(BackendRole.Admin)
    );
  }

  private getDefaultGermanText(companyDisplayName: string): string {
    return `Hallo!
Du wurdest von ${companyDisplayName} zu einer Videofernhilfe eingeladen.
Link zum Termin:`;
  }

  private trimToNull(value: string | null | undefined): string | null {
    const trimmed = value?.trim();
    return trimmed ? trimmed : null;
  }
}
